/**
 * Account Settings Page
 * Self-service security settings for ALL admin users
 */
import { useTranslation } from "react-i18next";
import { useState, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Label,
  Badge,
  Skeleton,
  toast,
} from "@jourfx/ui";
import { useSession, useBetterAuthSession, authClient } from "@/lib/auth";
import { api } from "@jourfx/convex/client";
import { MIN_PASSWORD_LENGTH } from "@jourfx/convex/lib/password";
import {
  Eye,
  EyeOff,
  Loader2,
  Monitor,
  Smartphone,
  LogOut,
  Shield,
  CheckCircle2,
  AlertCircle,
  User,
} from "lucide-react";

export function Account() {
  const { t } = useTranslation("account");
  const { data: session, isSuperAdmin, organizationName } = useSession();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
        <p className="text-muted-foreground">{t("subtitle")}</p>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t("profile.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("profile.name")}
              </label>
              <p className="text-lg">{session?.user?.name || t("placeholder")}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("profile.email")}
              </label>
              <p className="text-lg">{session?.user?.email || t("placeholder")}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("profile.role")}
              </label>
              <p className="text-lg">
                <Badge variant={isSuperAdmin ? "default" : "secondary"}>
                  {isSuperAdmin ? t("profile.superAdmin") : t("profile.administrator")}
                </Badge>
              </p>
            </div>
            {organizationName && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t("profile.organization")}
                </label>
                <p className="text-lg">{organizationName}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t("security.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Change Password Section */}
          <ChangePasswordSection />

          {/* Divider */}
          <div className="border-t" />

          {/* Active Sessions Section */}
          <ActiveSessionsSection />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Change Password Form Component
 */
function ChangePasswordSection() {
  const { t } = useTranslation("account");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setSuccess(false);

      // Validation
      if (!currentPassword) {
        setError(t("security.changePassword.currentRequired"));
        return;
      }

      if (!newPassword) {
        setError(t("security.changePassword.newRequired"));
        return;
      }

      if (newPassword.length < MIN_PASSWORD_LENGTH) {
        setError(t("security.changePassword.tooShort"));
        return;
      }

      if (newPassword !== confirmPassword) {
        setError(t("security.changePassword.mismatch"));
        return;
      }

      setIsLoading(true);

      try {
        const result = await authClient.changePassword({
          currentPassword,
          newPassword,
        });

        if (result.error) {
          setError(t("security.changePassword.error"));
          return;
        }

        setSuccess(true);
        // Clear form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        toast({
          title: t("security.changePassword.success"),
          variant: "default",
        });
      } catch {
        setError(t("security.changePassword.error"));
      } finally {
        setIsLoading(false);
      }
    },
    [currentPassword, newPassword, confirmPassword, t]
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">
          {t("security.changePassword.title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("security.changePassword.description")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        {/* Current Password */}
        <div className="space-y-2">
          <Label htmlFor="current-password">
            {t("security.changePassword.currentPassword")}
          </Label>
          <div className="relative">
            <Input
              id="current-password"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isLoading}
              className="pr-10"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showCurrentPassword ? t("security.changePassword.hideCurrentPassword") : t("security.changePassword.showCurrentPassword")}
              aria-pressed={showCurrentPassword}
            >
              {showCurrentPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div className="space-y-2">
          <Label htmlFor="new-password">
            {t("security.changePassword.newPassword")}
          </Label>
          <div className="relative">
            <Input
              id="new-password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
              className="pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showNewPassword ? t("security.changePassword.hideNewPassword") : t("security.changePassword.showNewPassword")}
              aria-pressed={showNewPassword}
            >
              {showNewPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirm-password">
            {t("security.changePassword.confirmPassword")}
          </Label>
          <div className="relative">
            <Input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              className="pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showConfirmPassword ? t("security.changePassword.hideConfirmPassword") : t("security.changePassword.showConfirmPassword")}
              aria-pressed={showConfirmPassword}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="flex items-center gap-2 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" />
            {t("security.changePassword.success")}
          </div>
        )}

        {/* Submit Button */}
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("security.changePassword.submit")}
        </Button>
      </form>
    </div>
  );
}

// Type for session from listMySessions
type SessionInfo = {
  id: string;
  userAgent: string;
  ipAddress: string;
  createdAt: number;
  expiresAt: number;
};

/**
 * Active Sessions List Component
 */
function ActiveSessionsSection() {
  const { t } = useTranslation("account");

  // Get session info from Better Auth (for current session ID)
  const { data: betterAuthSession } = useBetterAuthSession();

  // Get sessions from Convex
  const sessions = useQuery(api.auth.sessions.listMySessions) as
    | SessionInfo[]
    | undefined;
  const revokeSession = useMutation(api.auth.sessions.revokeMySession);
  const revokeAllOther = useMutation(api.auth.sessions.revokeAllMyOtherSessions);

  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(
    null
  );
  const [isRevokingAll, setIsRevokingAll] = useState(false);

  // Get current session ID to identify the current session
  // Using session ID instead of token to avoid exposing sensitive tokens
  const currentSessionId = betterAuthSession?.session?.id;

  const handleRevokeSession = useCallback(
    async (sessionId: string) => {
      if (!currentSessionId) {
        toast({
          title: t("security.sessions.error"),
          variant: "destructive",
        });
        return;
      }
      setRevokingSessionId(sessionId);
      try {
        await revokeSession({ sessionId, currentSessionId });
        toast({
          title: t("security.sessions.revokeSuccess"),
          variant: "default",
        });
      } catch {
        toast({
          title: t("security.sessions.error"),
          variant: "destructive",
        });
      } finally {
        setRevokingSessionId(null);
      }
    },
    [revokeSession, t, currentSessionId]
  );

  const handleRevokeAllOther = useCallback(async () => {
    if (!currentSessionId) return;

    setIsRevokingAll(true);
    try {
      await revokeAllOther({ currentSessionId });
      toast({
        title: t("security.sessions.revokeAllSuccess"),
        variant: "default",
      });
    } catch {
      toast({
        title: t("security.sessions.error"),
        variant: "destructive",
      });
    } finally {
      setIsRevokingAll(false);
    }
  }, [revokeAllOther, currentSessionId, t]);

  // Parse user agent to get device info
  const parseUserAgent = (userAgent: string) => {
    const ua = userAgent.toLowerCase();
    let device = "Desktop";
    let browser = "Unknown";

    // Detect device type
    if (ua.includes("mobile") || ua.includes("android")) {
      device = "Mobile";
    } else if (ua.includes("tablet") || ua.includes("ipad")) {
      device = "Tablet";
    }

    // Detect browser
    if (ua.includes("edg")) {
      browser = "Edge";
    } else if (ua.includes("chrome")) {
      browser = "Chrome";
    } else if (ua.includes("firefox")) {
      browser = "Firefox";
    } else if (ua.includes("safari")) {
      browser = "Safari";
    }

    return { device, browser };
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if a session is the current one
  const isCurrentSession = (sessionId: string) => {
    return sessionId === currentSessionId;
  };

  // Count other sessions
  const otherSessionsCount =
    sessions?.filter((s) => !isCurrentSession(s.id)).length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">
            {t("security.sessions.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("security.sessions.description")}
          </p>
        </div>
        {otherSessionsCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRevokeAllOther}
            disabled={isRevokingAll || !currentSessionId}
            title={!currentSessionId ? t("security.sessions.loadingSession") : undefined}
          >
            {isRevokingAll && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("security.sessions.revokeAll")}
          </Button>
        )}
      </div>

      {/* Loading State */}
      {sessions === undefined && (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {/* Sessions List */}
      {sessions && (
        <div className="space-y-3">
          {sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("security.sessions.noOtherSessions")}
            </p>
          ) : (
            sessions.map((session) => {
              const { device, browser } = parseUserAgent(session.userAgent);
              const isCurrent = isCurrentSession(session.id);
              const isRevoking = revokingSessionId === session.id;

              return (
                <div
                  key={session.id}
                  className={`flex items-center justify-between rounded-lg border p-4 ${
                    isCurrent ? "border-primary bg-primary/5" : ""
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Device Icon */}
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                      {device === "Mobile" || device === "Tablet" ? (
                        <Smartphone className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <Monitor className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>

                    {/* Session Info */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {browser} on {device}
                        </span>
                        {isCurrent && (
                          <Badge variant="default" className="text-xs">
                            {t("security.sessions.current")}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {session.ipAddress !== "Unknown" && (
                          <span>{session.ipAddress} - </span>
                        )}
                        <span>{formatDate(session.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Revoke Button (not for current session) */}
                  {!isCurrent && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={isRevoking}
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      {isRevoking ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <LogOut className="mr-2 h-4 w-4" />
                          {t("security.sessions.revoke")}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
